/**
 * CreatorIdeaDetailModal
 *
 * Creator context chrome selector. Renders the idea detail UI inside a
 * side drawer (bottom sheet on mobile, right panel on desktop) instead of
 * the admin centered modal. Keeps the filename so existing Creator call
 * sites (CreatorIdeasTab, CreatorBriefDetail) don't need to change imports.
 *
 * Context-keyed, not viewport-keyed — see IdeaDetailDrawer for the
 * rationale and link to the prior stacked-portals bug (commit d056427).
 */

import React from "react";
import { IdeaDetailDrawer } from "@/domains/Ideas/components/IdeaDetailDrawer";
import { UploadIcon } from "@/components/icons";
import { useLocale } from "@/hooks/useLocale";

interface CreatorIdeaDetailModalProps {
  ideaId: string | null;
  clientId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Upload media against the open idea (creator portal). */
  onUpload?: (ideaId: string) => void;
}

export const CreatorIdeaDetailModal: React.FC<CreatorIdeaDetailModalProps> = ({
  ideaId,
  clientId,
  isOpen,
  onClose,
  onUpload,
}) => {
  const { t } = useLocale();
  return (
    <IdeaDetailDrawer
      ideaId={ideaId}
      clientId={clientId}
      isOpen={isOpen}
      onClose={onClose}
      hideInternalResources
      footerExtra={
        onUpload && ideaId ? (
          <button
            type="button"
            onClick={() => onUpload(ideaId)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md transition-colors"
          >
            <UploadIcon className="w-3.5 h-3.5" />
            {t("creator.idea.upload")}
          </button>
        ) : undefined
      }
    />
  );
};
